import {
  ATLAS_AI_CANCELLED,
  ATLAS_AI_COMPLETE,
  ATLAS_AI_FAILED,
  ATLAS_AI_SUBAGENT_STATUS,
  ATLAS_AI_THINKING,
  ATLAS_AI_TURN_TEXT,
  ATLAS_AI_TYPING_START,
  ATLAS_AI_TYPING_STOP,
  CHANNEL_ARC_WROTE,
  APPROVAL_RESOLVED,
  ARC_ACTIVITY,
  ARC_NAVIGATE,
  CHANNEL_MESSAGE_CREATED,
} from '@common/constants/webSockets'
import { IArcActivity, IArcNavigation } from '@common/interfaces/arcPresence'
import {
  ApprovalStatus,
  IArcExecutedService,
  ISubagentStatus,
  isActiveSubagentStatus,
} from '@common/interfaces/atlasai'
import { ChannelMessageKind, IChannelMessage } from '@common/interfaces/channels'
import { Action } from 'redux'
import { Task } from 'redux-saga'
import { cancel, delay, fork, put, select, takeEvery } from 'redux-saga/effects'
import { arcPresence, IArcApprovalResolution } from '../../helpers/arcPresence/arcPresenceStore'
import { nonFatal } from '../../helpers/redux'
import { clearSubagentPanel } from './actions'
import {
  claimTurnDomain,
  refreshAfterArcWrite,
  refreshArcDomain,
  refreshCalendars,
} from './arcLiveRefresh'
import { ARC_WRITE_REFETCH, ArcRefetchDomain } from './arcWriteRefetch'
import { subagentsForSession } from './selectors'

export { refreshAutomationsAfterMutation } from './automations/sagas'
export { refreshDocsAfterMutation } from './docs/sagas'
export { refreshMemoriesAfterMutation } from './memories/sagas'
export { refreshPromptsAfterMutation } from './prompts/sagas'

interface IAtlasAICompleteAction extends Action {
  data: { sessionId?: string; messageId?: string; executedServices?: IArcExecutedService[] }
}

// When Arc finishes a turn that wrote data (commentary, note, task), silently
// refetch the affected page so the change shows without a manual reload. The
// concrete write services are on the COMPLETE payload's `executedServices`;
// anything outside the ARC_WRITE_REFETCH allowlist never triggers a refetch.
// A domain one of the turn's writes already refreshed as it landed is skipped.
export function* handleArcWriteComplete(action: IAtlasAICompleteAction) {
  const executed = action.data?.executedServices
  if (!executed?.length) return
  const turnId = action.data.messageId
  const domains = new Set<ArcRefetchDomain>()
  for (const { service } of executed) {
    const domain = ARC_WRITE_REFETCH[service]
    if (domain) domains.add(domain)
  }
  for (const domain of domains) {
    if (turnId && !claimTurnDomain(turnId, domain, Date.now())) continue
    yield* refreshArcDomain(domain, null)
  }
}

export function* refetchOnArcWrite() {
  yield takeEvery([ATLAS_AI_COMPLETE, CHANNEL_ARC_WROTE], handleArcWriteComplete)
}

type PresenceHandler<T> = (action: Action & { data: T }) => Generator<unknown, void, unknown> | void

/** One Arc presence event, made non-fatal (see `nonFatal`); the screen catches up later. */
export const nonFatalPresence = <T>(event: string, handle: PresenceHandler<T>) =>
  nonFatal<Action & { data: T }>(`Arc presence ${event}`, handle)

/** Arc's actions as they happen, into the store the screen's rows read (helpers/arcPresence). */
export function* feedArcPresence() {
  yield takeEvery(
    ARC_ACTIVITY,
    nonFatalPresence<IArcActivity>(ARC_ACTIVITY, function* ({ data }) {
      arcPresence.receive(data)
      yield* refreshAfterArcWrite(data)
    }),
  )
  yield takeEvery(
    APPROVAL_RESOLVED,
    nonFatalPresence<IArcApprovalResolution>(APPROVAL_RESOLVED, function* ({ data }) {
      arcPresence.resolveApproval(data)
      // An approved calendar delete never reaches ARC_ACTIVITY as a finished write.
      if (data.status === ApprovalStatus.Executed) yield* refreshCalendars()
    }),
  )
  yield takeEvery(
    CHANNEL_MESSAGE_CREATED,
    nonFatalPresence<{ channelId: string; message: IChannelMessage }>(
      CHANNEL_MESSAGE_CREATED,
      ({ data }) => {
        const { message } = data
        if (message.kind !== ChannelMessageKind.Arc) return
        arcPresence.receiveArcReply({
          channelId: data.channelId,
          arcTurnId: message.arcTurnId,
          voiceCallMessageId: message.voiceCallMessageId,
          content: message.content,
        })
      },
    ),
  )
  // The event reaches every tab the person has open; only the one they're looking at moves.
  yield takeEvery(
    ARC_NAVIGATE,
    nonFatalPresence<IArcNavigation>(ARC_NAVIGATE, ({ data }) => {
      if (document.visibilityState === 'hidden') return
      arcPresence.requestNavigation(data.url, data.label, data.chatHistoryId)
    }),
  )
}

// ---------------------------------------------------------------------------
// Orphaned subagent-panel watchdog
// ---------------------------------------------------------------------------
// When every detached subagent finishes, the panel enters an "analyzing
// results…" phase and stays mounted until the parent's synthesis turn ends and
// emits a terminal websocket event (ATLAS_AI_COMPLETE / _FAILED / _TYPING_STOP)
// — that's the only thing the reducer clears the analyzing panel on. But
// several server-side wake-turn exit paths emit no such event (0-row
// conditional writes, a soft-deleted session, a failed error-write), and a
// dropped ATLAS_AI_SUBAGENT_STATUS push has the same effect — leaving the panel
// stuck forever. This watchdog bounds that: once the panel is analyzing, it
// force-clears after a quiet window. Live synthesis output re-arms the timer so
// a legitimately-long turn is never cut off; a terminal event disarms it (the
// reducer clears the panel in that case).

const ORPHAN_PANEL_TIMEOUT_MS = 30_000

// Per-session watchdog tasks, keyed by sessionId.
const panelWatchdogs = new Map<string, Task>()

function* runPanelWatchdog(sessionId: string) {
  yield delay(ORPHAN_PANEL_TIMEOUT_MS)
  yield put(clearSubagentPanel(sessionId))
  panelWatchdogs.delete(sessionId)
}

function* armPanelWatchdog(sessionId: string) {
  const existing = panelWatchdogs.get(sessionId)
  if (existing) yield cancel(existing)
  const task = (yield fork(runPanelWatchdog, sessionId)) as Task
  panelWatchdogs.set(sessionId, task)
}

function* disarmPanelWatchdog(sessionId: string) {
  const existing = panelWatchdogs.get(sessionId)
  if (!existing) return
  yield cancel(existing)
  panelWatchdogs.delete(sessionId)
}

interface ISubagentStatusAction extends Action {
  data?: { sessionId?: string; subagents?: ISubagentStatus[] }
}
interface ISessionEventAction extends Action {
  data?: { sessionId?: string }
}

export function* watchOrphanedSubagentPanel() {
  // All subagents terminal → arm the watchdog; still-active (or empty) → disarm.
  yield takeEvery(ATLAS_AI_SUBAGENT_STATUS, function* (action: ISubagentStatusAction) {
    const sid = action.data?.sessionId
    if (!sid) return
    const items = action.data?.subagents ?? []
    const allTerminal = items.length > 0 && !items.some((s) => isActiveSubagentStatus(s.status))
    if (!allTerminal) {
      yield* disarmPanelWatchdog(sid)
      return
    }
    // Only arm if the reducer actually kept a panel up — it ignores an
    // all-terminal batch when nothing was ever shown running, and a stray late
    // push after a clean clear shouldn't spin up a pointless timer.
    const panel: ISubagentStatus[] = yield select(subagentsForSession, sid)
    if (panel.length > 0) yield* armPanelWatchdog(sid)
  })

  // Live synthesis output while analyzing → the wake turn is working; re-arm so
  // the timer measures SILENCE since last activity, not total elapsed time.
  yield takeEvery(
    [ATLAS_AI_TURN_TEXT, ATLAS_AI_THINKING, ATLAS_AI_TYPING_START],
    function* (action: ISessionEventAction) {
      const sid = action.data?.sessionId
      if (sid && panelWatchdogs.has(sid)) yield* armPanelWatchdog(sid)
    },
  )

  // Terminal / cancel event → the reducer clears the panel itself; just disarm.
  yield takeEvery(
    [ATLAS_AI_COMPLETE, ATLAS_AI_FAILED, ATLAS_AI_TYPING_STOP, ATLAS_AI_CANCELLED],
    function* (action: ISessionEventAction) {
      const sid = action.data?.sessionId
      if (sid) yield* disarmPanelWatchdog(sid)
    },
  )
}
